

import React, { useState, useEffect, useMemo, useRef } from "react"
// Cart functionality moved to direct Redux usage
import PhoneNumberPopup from "./PhoneNumberPopup"
import { useSelector, useDispatch } from "react-redux"
import type { RootState, AppDispatch } from "../store"
import { addItem } from "../features/cart/cartSlice"
// Product API functionality moved to slices
// import { fetchProductById } from "../services/api"
// import type { ProductApiResponse } from "../services/api/types"
import { useReduxLanguage } from "../hooks/useReduxLanguage"
import { useTheme } from "../contexts/ThemeContext"
import CaloriesIcon from "./icons/CaloriesIcon"
import { useTranslation } from "../hooks/useTranslation"
import CurrencyDisplay from "./ui/CurrencyDisplay"
import { isValidPhoneNumber } from "../config/countryConfig"


// Helper function to get product by ID from Redux store
const getProductById = (productId: number | string, categories: any[]) => {
  for (const category of categories) {
    const product = category.products?.find((p: any) => p.id === productId || p.id === Number(productId))
    if (product) {
      return product
    }
  }
  return null
}

// Helper function to transform API data for ProductCard
const transformApiDataForProductCard = (item: any) => {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    image: item.image,
    price: item.price,
    type: item.type || "product",
    additionals: item.additionals || [],
    itemable: {
      name: {
        ar: item.name,
        en: item.name,
      },
      description: item.description,
    },
  }
}

/** Returns a positive integer max_select, or null when absent/invalid (preserve unlimited behavior). */
const getValidMaxSelect = (maxSelect: unknown): number | null => {
  if (maxSelect === null || maxSelect === undefined || maxSelect === "") {
    return null
  }
  const parsed = typeof maxSelect === "number" ? maxSelect : Number(maxSelect)
  if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
    return null
  }
  return parsed
}

const getSelectedCountInGroup = (groupItems: any[], selectedIds: number[]): number => {
  const groupIds = new Set(
    groupItems.map((item: any) => parseInt(item.id.toString())).filter((id: number) => !isNaN(id))
  )
  return selectedIds.filter((id) => groupIds.has(id)).length
}

interface ProductModalProps {
  product: any
  onClose: () => void
  onAddToCart?: (item: any) => void
  isEditing?: boolean
  initialExtras?: any[]
}

export default function ProductModal({
  product,
  onClose,
  onAddToCart,
  isEditing = false,
  initialExtras = [],
}: ProductModalProps) {
  const { t, currentLanguage: language } = useTranslation()
  const { categories } = useSelector((state: RootState) => state.menu)
  const { primaryColor, canOrder } = useTheme()
  const [productData, setProductData] = useState(product)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isAddingToCartRef.current = false
    }
  }, [])

  // Close with animation
  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      // Reset guards when modal is closing
      isAddingToCartRef.current = false
      setLoading(false)
      setError(null)
      onClose()
    }, 300) // Wait for animation to complete
  }

  // Get current language for refetching
  const { currentLanguage: currentLang } = useTranslation()

  // Get product data from API - refetch when language changes
  useEffect(() => {
    if (isEditing) {
      return
    }

    if (product && product.id) {
      const fetchProductData = async () => {
        try {
          setLoading(true)
          setError(null)

          // Fetch detailed product data from API
          
          // Import API client
          const { apiClient } = await import("../base_url/config")
          
          // Get branch ID from URL params or default to 1
          const urlParams = new URLSearchParams(window.location.search)
          const branchId = urlParams.get('branchId') || '1'
          
          // Get auth token if available
          const token = localStorage.getItem('auth_token')
          
          const headers: any = {
            'Accept': 'application/json',
            'platform': 'qr',
            // lang will be added automatically by interceptor
          }
          
          if (token) {
            headers.Authorization = `Bearer ${token}`
          }
          
          const response = await apiClient.get(`/qr/products/${product.id}`, {
            params: { branchId: parseInt(branchId) },
            headers
          })
          
          const productDetails = response.data.data


          // Transform API data for the modal
          const transformedData = {
            ...product,
            ...productDetails,
            quantity: isEditing ? product.quantity : 1,
            extras: isEditing ? initialExtras : [],
            price: productDetails.price,
            meals: productDetails.meals || [],
          }

          setProductData(transformedData)
        } catch (err) {
          setError(t('errors.loadingError'))
          // Fallback to original product data
          setProductData(product)
        } finally {
          setLoading(false)
        }
      }

      fetchProductData()
    } else {
      // If no product ID, use original product data
      setProductData(product)
      setLoading(false)
    }
  }, [product.id, currentLang, isEditing]) // Updated dependencies

  const initialQuantity = isEditing && productData.quantity ? productData.quantity : 1
  const [quantity, setQuantity] = useState(initialQuantity)
  const [selectedExtras, setSelectedExtras] = useState(initialExtras || [])
  const [selectedMeal, setSelectedMeal] = useState<number | null>(null)
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [tempItem, setTempItem] = useState<any>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  // Group error state - track which groups have validation errors
  const [groupErrors, setGroupErrors] = useState<{ [key: string]: boolean }>({})
  // Refs for scrolling to groups with validation errors
  const groupRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement | null> }>({})
  // Simple guard against double submission
  const isAddingToCartRef = useRef(false)

  // Redux cart functionality replaced with direct dispatch
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, phoneNumber, token: authToken } = useSelector((state: RootState) => state.auth)
  const { formatNum } = useReduxLanguage()
  

  // Get additionals from meals structure
  const additionalGroups = useMemo(() => {
  
    
    const groups: { [key: string]: any } = {}

    // Check if we have meals with additionalsGroup
    if (productData.meals && productData.meals.length > 0) {
      // Only show additionals from the selected meal, or from all meals if no meal is selected
      const mealsToProcess = selectedMeal 
        ? productData.meals.filter((meal: any) => meal.id === selectedMeal)
        : productData.meals

      mealsToProcess.forEach((meal: any) => {
        if (meal.additionalsGroup && meal.additionalsGroup.length > 0) {
          meal.additionalsGroup.forEach((group: any) => {
            const groupName = group.group || t("menu.other")
            if (!groups[groupName]) {
              groups[groupName] = {
                items: [],
                is_required: group.is_required || false,
                is_multi_select: group.is_multi_select !== undefined ? group.is_multi_select : true,
                max_select: getValidMaxSelect(group.max_select),
              }
            }

            if (group.additionals && group.additionals.length > 0) {
              group.additionals.forEach((item: any) => {
                // Avoid duplicates
                const exists = groups[groupName].items.some((existing: any) => existing.id === item.id)
                if (!exists) {
                  groups[groupName].items.push({
                    id: item.id,
                    name: item.name,
                    price: item.price || "0.00",
                    image: item.image || "/p.png",
                    calories: item.calories || 0,
                  })
                }
              })
            }
          })
        }
      })
    }

    // Fallback to old additionals structure if available
    const additionals = productData.additionals || []
    
    if (additionals.length > 0 && Object.keys(groups).length === 0) {
      additionals.forEach((item: any) => {
        if (!item) return

        if (item.additionalGroup && typeof item.additionalGroup === "object") {
          const groupName = item.additionalGroup.name || t("menu.other")
          if (!groups[groupName]) {
            groups[groupName] = {
              items: [],
              is_required: false,
              is_multi_select: true,
              max_select: null,
            }
          }
          groups[groupName].items.push({
            id: item.id,
            name: item.name,
            price: item.price || "0.00",
            image: item.image || "/p.png",
            calories: item.calories || 0,
          })
        } else {
          const groupName = t("menu.other")
          if (!groups[groupName]) {
            groups[groupName] = {
              items: [],
              is_required: false,
              is_multi_select: true,
              max_select: null,
            }
          }
          groups[groupName].items.push({
            id: item.id,
            name: item.name,
            price: item.price || "0.00",
            image: item.image || "/p.png",
            calories: item.calories || 0,
          })
        }
      })
    }

    return groups
  }, [productData.meals, productData.additionals, selectedMeal, t])

  // Auto-select first meal when data loads (but not additionals - let user choose from required groups)
  useEffect(() => {
    if (!isEditing && productData) {
      // Select first meal if available
      if (productData.meals && productData.meals.length > 0 && selectedMeal === null) {
        setSelectedMeal(productData.meals[0].id)
      }

      // Removed auto-selection of additionals to allow validation flow to work
      // User must manually select from required groups
    }
  }, [productData.meals, isEditing, selectedMeal])

  // Reset selectedExtras when selectedMeal changes
  useEffect(() => {
    if (selectedMeal && !isEditing) {
      // Clear previous selections since additionals may be different
      setSelectedExtras([])
      
      // Removed auto-selection to allow validation flow to work properly
      // User must manually select from required groups when meal changes
    }
  }, [selectedMeal, isEditing, productData.meals])

  const toggleExtra = (id: any) => {
    const numericId = parseInt(id.toString())

    if (isNaN(numericId)) {
      return
    }

    // Find which group this extra belongs to
    const extraGroup = Object.entries(additionalGroups).find(([_, group]: [string, any]) => 
      group.items.some((item: any) => parseInt(item.id.toString()) === numericId)
    )

    const groupData = extraGroup ? extraGroup[1] : null
    const groupName = extraGroup ? extraGroup[0] : null
    const isMultiSelect = groupData?.is_multi_select !== false
    const maxSelect = getValidMaxSelect(groupData?.max_select)

    setSelectedExtras((prev: any[]) => {
      // Convert all existing items to numbers for consistent comparison
      const normalizedPrev = prev.map(item => parseInt(item.toString())).filter(item => !isNaN(item))

      if (normalizedPrev.includes(numericId)) {
        // Always allow deselect of an already selected item
        return normalizedPrev.filter(item => item !== numericId)
      }

      if (isMultiSelect) {
        // Enforce max_select per group (selection logic, not UI-only)
        if (maxSelect !== null && groupData?.items) {
          const selectedInGroup = getSelectedCountInGroup(groupData.items, normalizedPrev)
          if (selectedInGroup >= maxSelect) {
            return normalizedPrev
          }
        }

        const result = Array.from(new Set([...normalizedPrev, numericId]))

        if (groupName && groupErrors[groupName]) {
          setGroupErrors(prevErrors => ({ ...prevErrors, [groupName]: false }))
        }

        return result
      }

      // Single-select: Remove other items from the same group
      const otherGroupIds = groupData?.items.map((item: any) => parseInt(item.id.toString())) || []
      const filtered = normalizedPrev.filter(item => !otherGroupIds.includes(item))
      const result = [...filtered, numericId]

      if (groupName && groupErrors[groupName]) {
        setGroupErrors(prevErrors => ({ ...prevErrors, [groupName]: false }))
      }

      return result
    })
  }

  const totalPrice = useMemo(() => {
    try {
      let basePrice = 0

      // Use selected meal price if available, otherwise use product price
      if (selectedMeal && productData.meals) {
        const selectedMealData = productData.meals.find((meal: any) => meal.id === selectedMeal)
        if (selectedMealData && selectedMealData.price) {
          basePrice = Number.parseFloat(selectedMealData.price) || 0
        }
      } else if (productData.price !== null && productData.price !== undefined) {
        basePrice = Number.parseFloat(productData.price) || 0
      }

      let extrasPrice = 0
      
      // Calculate extras price directly from selected meal data
      if (selectedMeal && selectedExtras.length > 0 && productData.meals) {
        const selectedMealData = productData.meals.find((meal: any) => meal.id === selectedMeal)
        if (selectedMealData?.additionalsGroup) {
          selectedExtras.forEach((extraId) => {
            // Find the extra in the selected meal's additionals
            let foundExtra = null
            for (const group of selectedMealData.additionalsGroup) {
              if (group.additionals) {
                foundExtra = group.additionals.find((item: any) => 
                  item.id === extraId || item.id === Number.parseInt(extraId) || Number.parseInt(item.id) === extraId
                )
                if (foundExtra) break
              }
            }
            
            if (foundExtra && foundExtra.price) {
              const extraPrice = Number.parseFloat(foundExtra.price)
              if (!isNaN(extraPrice)) {
                extrasPrice += extraPrice
              } else {
                extrasPrice += 1
              }
            } else {
              extrasPrice += 1
            }
          })
        }
      } else {
        extrasPrice = selectedExtras.length * 1
      }

      return basePrice + extrasPrice
    } catch (error) {
      return Number.parseFloat(productData.price) || 0
    }
  }, [productData.price, productData.meals, selectedMeal, selectedExtras])

  // Calculate total calories
  const totalCalories = useMemo(() => {
    try {
      let mealCalories = 0

      // Get calories from selected meal
      if (selectedMeal && productData.meals) {
        const selectedMealData = productData.meals.find((meal: any) => meal.id === selectedMeal)
        if (selectedMealData && selectedMealData.calories) {
          mealCalories = Number.parseInt(selectedMealData.calories) || 0
        }
      }

      // Calculate calories from selected additionals directly from selected meal data
      let additionalsCalories = 0
      
      if (selectedMeal && selectedExtras.length > 0 && productData.meals) {
        const selectedMealData = productData.meals.find((meal: any) => meal.id === selectedMeal)
        if (selectedMealData?.additionalsGroup) {
          selectedExtras.forEach((extraId) => {
            // Find the extra in the selected meal's additionals
            let foundExtra = null
            for (const group of selectedMealData.additionalsGroup) {
              if (group.additionals) {
                foundExtra = group.additionals.find((item: any) => 
                  item.id === extraId || item.id === Number.parseInt(extraId) || Number.parseInt(item.id) === extraId
                )
                if (foundExtra) break
              }
            }
            
            if (foundExtra && foundExtra.calories !== undefined) {
              const extraCalories = Number.parseInt(foundExtra.calories) || 0
              additionalsCalories += extraCalories
            }
          })
        }
      }

      return mealCalories + additionalsCalories
    } catch (error) {
      return 0
    }
  }, [productData.meals, selectedMeal, selectedExtras])

  // Handle successful phone login - add the temp item to cart
  const handlePhoneLoginSuccess = async (item: any) => {
    
    try {
      // Wait a bit for Redux state to update after login
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Get token from Redux state (more reliable than localStorage)
      const token = authToken || localStorage.getItem('auth_token')
      
      if (token) {
        // Use API to add item to cart
        const { addItemToCart, fetchCart } = await import("../features/cart/cartSlice")
        const result = await dispatch(addItemToCart({ item, token }))
        
        if (addItemToCart.fulfilled.match(result)) {
          // Refresh cart data from server
          await dispatch(fetchCart(token))
    
        } else {
          dispatch(addItem(item))
        }
      } else {
        // Fallback: Add to local cart
        dispatch(addItem(item))
      }
    } catch (error) {
      // Fallback: Add to local cart
      dispatch(addItem(item))
    }
    
    // إغلاق نافذة الهاتف والعودة لنافذة المنتج
    setShowPhonePopup(false)
    setTempItem(null)
    
    // عرض رسالة نجاح لثوانٍ قليلة
    setShowSuccessMessage(true)
    setTimeout(() => {
      setShowSuccessMessage(false)
    }, 3000) // إخفاء الرسالة بعد 3 ثواني
    
    // لا نستدعي handleClose() هنا لنبقي نافذة المنتج مفتوحة
  }

  // Validation function for additionals - returns invalid group name if validation fails
  const validateAdditions = (): { isValid: boolean; invalidGroupName: string | null } => {
    const normalizedSelected = selectedExtras
      .map((item: any) => parseInt(item.toString()))
      .filter((id: number) => !isNaN(id))

    for (const [groupName, groupData] of Object.entries(additionalGroups)) {
      const group = groupData as any

      if (!group.is_required) {
        continue
      }

      const selectedCount = getSelectedCountInGroup(group.items, normalizedSelected)
      const maxSelect = getValidMaxSelect(group.max_select)

      // Required + valid max_select: must select exactly max_select (cannot exceed due to toggleExtra)
      if (maxSelect !== null) {
        if (selectedCount < maxSelect) {
          return {
            isValid: false,
            invalidGroupName: groupName,
          }
        }
        continue
      }

      // Required without max_select: existing behavior — at least one selection
      if (selectedCount < 1) {
        return {
          isValid: false,
          invalidGroupName: groupName,
        }
      }
    }

    return { isValid: true, invalidGroupName: null }
  }

  const handleAddToCart = async () => {
    // Simple protection against double clicks
    if (loading || isAddingToCartRef.current) {
      return
    }

    // Set guard immediately
    isAddingToCartRef.current = true
    setLoading(true)

    try {
      // Validate additionals before proceeding
      const validation = validateAdditions()
      if (!validation.isValid && validation.invalidGroupName) {
        // Set error state for the invalid group
        setGroupErrors(prev => ({ ...prev, [validation.invalidGroupName!]: true }))
        
        // Get or create ref for the invalid group
        if (!groupRefs.current[validation.invalidGroupName]) {
          groupRefs.current[validation.invalidGroupName] = { current: null }
        }
        
        // Scroll to the invalid group
        setTimeout(() => {
          groupRefs.current[validation.invalidGroupName!]?.current?.scrollIntoView({ 
            behavior: "smooth", 
            block: "center" 
          })
        }, 0)
        
        isAddingToCartRef.current = false
        setLoading(false)
        return
      }

      const item = {
        ...productData,
        quantity,
        extras: selectedExtras,
        selectedMeal,
        totalPrice: totalPrice * quantity,
        meals: productData.meals || [],
      }

      // Validate required data
      if (!item.id) {
        setError(t("menu.productDataError"))
        return
      }

      // For products with meals, ensure a meal is selected
      if (productData.meals && productData.meals.length > 0 && selectedMeal === null) {
        setError(t("menu.selectMealSize"))
        return
      }

      // Ensure selectedMeal is set for products with meals
      if (productData.meals && productData.meals.length > 0 && selectedMeal === null) {
        // Auto-select first meal if none selected
        item.selectedMeal = productData.meals[0].id
      }


      // Additional debugging for meals
      if (item.meals && item.meals.length > 0) {
 
      } else {
      }


      // Handle edit mode
      if (isEditing && onAddToCart) {
        onAddToCart(item)
        handleClose()
        return
      }

      // Check if user is authenticated with a valid regional phone number
      // SA: 9 digits (5XXXXXXXX), EG: 11 digits (01XXXXXXXXX)
      if (isAuthenticated && phoneNumber && isValidPhoneNumber(phoneNumber)) {
        try {
          const token = authToken || localStorage.getItem('auth_token')
          if (token) {
            
            // Use API to add item to cart
            const { addItemToCart, fetchCart } = await import("../features/cart/cartSlice")
            const result = await dispatch(addItemToCart({ item, token }))
            
            if (addItemToCart.fulfilled.match(result)) {
              // Refresh cart data from server
              await dispatch(fetchCart(token))
            } else {
              dispatch(addItem(item))
            }
          } else {
            // Fallback: Add to local cart
            dispatch(addItem(item))
          }
        } catch (error) {
          // Fallback: Add to local cart
          dispatch(addItem(item))
        }
        handleClose()
      } else {
        // Show phone popup for unauthenticated users
        setTempItem(item)
        setShowPhonePopup(true)
      }
    } catch (error) {
      setError(t("menu.addToCartError"))
    } finally {
      // Reset guards
      isAddingToCartRef.current = false
      setLoading(false)
    }
  }

  const handlePhoneSubmit = async (phoneNumber: string) => {
    // Simple protection
    if (loading || isAddingToCartRef.current) {
      return
    }

    // Set guards
    isAddingToCartRef.current = true
    setLoading(true)

    try {

    } catch (error) {
      setError(t("menu.registrationError"))
    } finally {
      // Reset guards
      isAddingToCartRef.current = false
      setLoading(false)
    }
  }

  if (showPhonePopup) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="w-full max-w-md mx-4">
          <PhoneNumberPopup onClose={() => setShowPhonePopup(false)} onSubmit={handlePhoneSubmit} />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div
        className={`w-full h-[90%] bg-white rounded-t-[15px] pt-3 pr-3 pb-3 pl-3 flex flex-col transition-transform duration-300 ease-out ${isVisible ? "translate-y-0" : "translate-y-full"
          }`}
      >
        {/* Close Button */}
        <div className="flex justify-between text-center items-center">
          <button
            onClick={handleClose}
            className={`text-gray-500 hover:text-gray-700 border border-gray-300 rounded-xl p-2.5 ${(loading || isAddingToCartRef.current) ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={loading || isAddingToCartRef.current}
          >
            {(loading || isAddingToCartRef.current) ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
          
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-2 rounded-lg animate-pulse">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">{t("menu.itemAddedSuccessfully")}</span>
            </div>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-1">
          {loading ? (
            // عرض مؤشر التحميل
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mb-4" style={{ borderColor: primaryColor }}></div>
              <p className="text-gray-600">{t("menu.loadingProduct")}</p>
            </div>
          ) : error ? (
            // عرض رسالة الخطأ
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-red-500 mb-4 text-center text-lg">{error}</p>
              <p className="text-gray-600 mb-4 text-center">{t("menu.productLoadError")}</p>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("common.close")}
              </button>
            </div>
          ) : (
            <>
              {/* Product Info */}
              <div className="mb-6">
                <img
                  src={productData.image || "/placeholder.png"}
                  alt={productData.name}
                  className="mx-auto w-[230px] h-38 object-cover mb-2"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.src = "/placeholder.png"
                  }}
                />
                <h2 className="text-lg font-bold">{productData.name}</h2>
                <p className="text-sm text-gray-500">{productData.description || ""}</p>

                {/* Show meal calories if available */}
                {selectedMeal && productData.meals && (() => {
                  const selectedMealData = productData.meals.find((meal: any) => meal.id === selectedMeal)
                  const mealCalories = selectedMealData?.calories ? Number.parseInt(selectedMealData.calories) || 0 : 0
                  return mealCalories > 0 ? (
                    <div className="flex items-center gap-1 mt-1">
                      <CaloriesIcon width={16} height={16} color={primaryColor} />
                      <p className="text-sm font-medium" style={{ color: primaryColor }}>
                        {formatNum(mealCalories)} {t("menu.caloriesUnit")}
                      </p>
                    </div>
                  ) : null
                })()}
                <p className="font-semibold mt-2 flex items-center justify-start gap-1.5" style={{ color: primaryColor }} dir="ltr">
                  <CurrencyDisplay className="font-semibold" iconStyle={{ color: primaryColor }}>
                  <svg width="16" height="19" viewBox="0 0 16 19" fill="none">
                    <path d="M15.9999 15.0621C15.8942 15.9172 15.8479 16.2882 15.4531 17.1217L9.38959 18.3737C9.52897 17.4728 9.71455 16.7777 10.0165 16.361L15.9999 15.0621ZM7.55463 9.16565L9.36615 8.77307V3.04749C10.0413 2.28964 10.4565 1.94932 11.2714 1.51917V8.35901L15.9999 7.33264C15.8942 8.1875 15.8478 8.55802 15.4531 9.39124L11.2714 10.275V12.1989L15.9999 11.1979C15.8942 12.053 15.848 12.4239 15.4531 13.2574L11.2714 14.1207V14.1383L9.36615 14.5319V10.6774L7.55463 11.0602V13.4899L7.52338 13.4957C7.10666 14.2265 6.51821 15.1044 5.95111 15.8053L-6.10352e-05 16.9391C0.0533004 16.1734 0.164418 15.7416 0.510681 14.9703L5.64935 13.8561V11.4635L0.886658 12.4713C0.940015 11.7056 1.05218 11.2747 1.39838 10.5035L5.64935 9.57971V1.98206C6.32448 1.22421 6.73971 0.883893 7.55463 0.453735V9.16565Z" fill={primaryColor}/>
                  </svg>
                  </CurrencyDisplay>
                  <span>{formatNum(productData.price || 0)}</span>
                </p>
              </div>

              {/* Show meals if available */}
              {productData.meals && productData.meals.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold mb-5">{t("menu.size")}</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
                    {productData.meals.map((meal: any) => (
                      <label
                        key={meal.id}
                        className={`relative border p-2 pb-5 pt-7 mb-5 rounded-xl text-center cursor-pointer ${selectedMeal === meal.id ? 'border-gray-300' : 'border-gray-300'
                          }`}
                        style={selectedMeal === meal.id ? { borderColor: primaryColor } : {}}
                        onClick={() => setSelectedMeal(meal.id)}
                      >
                        {/* Circle indicator - always visible */}
                        <div className="absolute top-0 right-0 w-5 h-5 rounded-full border-2 flex items-center justify-center m-1 z-10"
                          style={selectedMeal === meal.id ? { borderColor: primaryColor } : { borderColor: '#d1d5db' }}>
                          {/* Dot only visible when selected */}
                          {selectedMeal === meal.id && (
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                          )}
                        </div>

                        <h2 className={`text-lg font-bold ${selectedMeal === meal.id ? '' : 'text-gray-900'}`}
                          style={selectedMeal === meal.id ? { color: primaryColor } : {}}>
                          {meal.size?.name || t("menu.meal")}
                        </h2>
                        <p className="text-sm text-gray-500">{meal.description || ""}</p>
                        {Number(meal.price) > 0 && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ">
                            <div className={`px-3 py-1 rounded-full border text-xs font-bold shadow-sm flex items-center justify-center gap-1.5 ${selectedMeal === meal.id
                              ? 'bg-white border-gray-300'
                              : 'bg-white text-gray-800 border-gray-300'
                              }`}
                              style={selectedMeal === meal.id ? { color: primaryColor, borderColor: primaryColor } : {}}
                              dir="ltr">
                              <CurrencyDisplay iconStyle={{ color: selectedMeal === meal.id ? primaryColor : '#6b7280' }}>
                              <svg width="12" height="14" viewBox="0 0 16 19" fill="none">
                                <path d="M15.9999 15.0621C15.8942 15.9172 15.8479 16.2882 15.4531 17.1217L9.38959 18.3737C9.52897 17.4728 9.71455 16.7777 10.0165 16.361L15.9999 15.0621ZM7.55463 9.16565L9.36615 8.77307V3.04749C10.0413 2.28964 10.4565 1.94932 11.2714 1.51917V8.35901L15.9999 7.33264C15.8942 8.1875 15.8478 8.55802 15.4531 9.39124L11.2714 10.275V12.1989L15.9999 11.1979C15.8942 12.053 15.848 12.4239 15.4531 13.2574L11.2714 14.1207V14.1383L9.36615 14.5319V10.6774L7.55463 11.0602V13.4899L7.52338 13.4957C7.10666 14.2265 6.51821 15.1044 5.95111 15.8053L-6.10352e-05 16.9391C0.0533004 16.1734 0.164418 15.7416 0.510681 14.9703L5.64935 13.8561V11.4635L0.886658 12.4713C0.940015 11.7056 1.05218 11.2747 1.39838 10.5035L5.64935 9.57971V1.98206C6.32448 1.22421 6.73971 0.883893 7.55463 0.453735V9.16565Z" fill={selectedMeal === meal.id ? primaryColor : '#6b7280'}/>
                              </svg>
                              </CurrencyDisplay>
                              <span>{formatNum(meal.price)}</span>
                            </div>
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* عرض مجموعات الإضافات فقط إذا كانت متوفرة من API */}
              {Object.keys(additionalGroups).length > 0 && (
                Object.entries(additionalGroups).map(([groupName, groupData]) => {
                  const group = groupData as any
                  const items = group.items
                  const maxSelect = getValidMaxSelect(group.max_select)
                  const selectedInGroupCount = getSelectedCountInGroup(
                    items,
                    selectedExtras
                      .map((item: any) => parseInt(item.toString()))
                      .filter((id: number) => !isNaN(id))
                  )
                  const isAtMaxSelect =
                    group.is_multi_select && maxSelect !== null && selectedInGroupCount >= maxSelect
                  
                  // Initialize ref for this group if not exists
                  if (!groupRefs.current[groupName]) {
                    groupRefs.current[groupName] = { current: null }
                  }
                  
                  return (
                  <div 
                    key={groupName} 
                    className="mb-6"
                    ref={groupRefs.current[groupName]}
                  >
                    <div className="flex items-center gap-2 mb-5">
                      <h3 className="text-sm font-semibold">{groupName}</h3>
                      {group.is_required && <span className="text-red-500">*</span>}
                      {groupErrors[groupName] ? (
                        <span className="text-xs text-red-500 border-2 border-red-200 bg-red-50 rounded px-2 py-1">
                          {t("menu.required")}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {group.is_multi_select ? t("menu.canSelectMultiple") : t("menu.selectOne")}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-4 gap-3">
                      {items.map((extra: any) => {
                        const extraId = parseInt(extra.id.toString())
                        const isSelected = selectedExtras.includes(extraId)
                        const isSelectionBlocked = isAtMaxSelect && !isSelected

                        return (
                        <label
                          key={extra.id}
                          className={`relative border p-3 pb-4 rounded-xl text-center ${
                            isSelectionBlocked
                              ? 'border-gray-300 cursor-not-allowed opacity-50'
                              : 'border-gray-300 cursor-pointer'
                          }`}
                          style={isSelected ? { borderColor: primaryColor } : {}}
                          onClick={(e) => {
                            if (isSelectionBlocked) {
                              e.preventDefault()
                              return
                            }
                            toggleExtra(extra.id)
                          }}
                        >
                          {/* Radio circle for single-select, checkbox for multi-select */}
                          {!group.is_multi_select ? (
                            <div className="absolute top-0 right-0 w-5 h-5 rounded-full border-2 flex items-center justify-center m-1 z-10"
                              style={isSelected ? { borderColor: primaryColor } : { borderColor: '#d1d5db' }}>
                              {/* Dot only visible when selected */}
                              {isSelected && (
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                              )}
                            </div>
                          ) : (
                            <div className={`absolute top-0 right-0 rounded-md w-6 h-6 flex items-center justify-center m-1 z-10 border-2 ${isSelected
                              ? 'border-transparent'
                              : 'border-gray-300 bg-white'
                              }`}
                              style={isSelected ? { backgroundColor: primaryColor } : {}}>
                              {/* Checkmark only visible when selected */}
                              {isSelected && (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          )}

                          {/* عرض الصورة إذا وجدت، أو أيقونة بسيطة إذا لم توجد */}
                          {extra.image && extra.image !== "/p.png" ? (
                            <img
                              src={extra.image}
                              alt={extra.name}
                              className="mx-auto w-[230px] h-38 object-cover mb-2"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                // منع التكرار اللانهائي - نستخدم data attribute لتتبع حالة الخطأ
                                if (!target.dataset.errorHandled) {
                                  target.dataset.errorHandled = 'true'
                                  target.src = "/placeholder.svg"
                                }
                              }}
                            />
                          ) : (
                            <div className="flex items-center justify-center mb-2">
                              <div className="text-4xl w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg">
                                🍽️
                              </div>
                            </div>
                          )}
                          <h2 className={`text-lg font-bold ${isSelected ? '' : 'text-gray-900'}`}
                            style={isSelected ? { color: primaryColor } : {}}>
                            {extra.name}
                          </h2>
                          <p className="text-sm text-gray-500">{extra.description || ""}</p>
                          {Number(extra.price) > 0 && (
                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ">
                              <div className={`px-3 py-1 rounded-full border text-xs font-bold shadow-sm flex items-center justify-center gap-1.5 ${isSelected
                                ? 'bg-white border-gray-300'
                                : 'bg-white text-gray-800 border-gray-300'
                                }`}
                                style={isSelected ? { color: primaryColor, borderColor: primaryColor } : {}}
                                dir="ltr">
                                <span>+</span>
                                <CurrencyDisplay iconStyle={{ color: isSelected ? primaryColor : '#6b7280' }}>
                                <svg width="12" height="14" viewBox="0 0 16 19" fill="none">
                                  <path d="M15.9999 15.0621C15.8942 15.9172 15.8479 16.2882 15.4531 17.1217L9.38959 18.3737C9.52897 17.4728 9.71455 16.7777 10.0165 16.361L15.9999 15.0621ZM7.55463 9.16565L9.36615 8.77307V3.04749C10.0413 2.28964 10.4565 1.94932 11.2714 1.51917V8.35901L15.9999 7.33264C15.8942 8.1875 15.8478 8.55802 15.4531 9.39124L11.2714 10.275V12.1989L15.9999 11.1979C15.8942 12.053 15.848 12.4239 15.4531 13.2574L11.2714 14.1207V14.1383L9.36615 14.5319V10.6774L7.55463 11.0602V13.4899L7.52338 13.4957C7.10666 14.2265 6.51821 15.1044 5.95111 15.8053L-6.10352e-05 16.9391C0.0533004 16.1734 0.164418 15.7416 0.510681 14.9703L5.64935 13.8561V11.4635L0.886658 12.4713C0.940015 11.7056 1.05218 11.2747 1.39838 10.5035L5.64935 9.57971V1.98206C6.32448 1.22421 6.73971 0.883893 7.55463 0.453735V9.16565Z" fill={isSelected ? primaryColor : '#6b7280'}/>
                                </svg>
                                </CurrencyDisplay>
                                <span>{formatNum(extra.price)}</span>
                              </div>
                            </div>
                          )}
                        </label>
                        )
                      })}
                    </div>
                  </div>
                  )
                })
              )}
            </>
          )}
        </div>

        {/* Footer Row - Only show when there's no error */}
        {!error && (
          <>
            {/* Total Calories Display - Above the entire footer section, aligned right */}
            {totalCalories > 0 && (
              <div className="flex justify-between items-center mb-2">
                <div></div> {/* Left empty for spacing */}
                <div className="text-sm font-medium flex items-center gap-1" style={{ color: primaryColor }}>
                  <CaloriesIcon width={16} height={16} color={primaryColor} />
                  <span>{formatNum(totalCalories)} {t("menu.caloriesUnit")}</span>
                </div>
              </div>
            )}

            {canOrder && (
            <div className="flex gap-2 mt-2">
              {/* عداد الكمية */}
              <div className="w-[40%] flex items-center border border-gray-300 rounded-xl overflow-hidden h-[45px] text-black text-lg font-medium">
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="flex-1 text-center border-l border-r h-[50px] border-gray-300"
                  disabled={loading}
                >
                  +
                </button>
                <span className="flex-1 text-center select-none">{formatNum(quantity)}</span>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="flex-1 text-center border-l border-r h-[50px] border-gray-300"
                  disabled={loading}
                >
                  –
                </button>
              </div>

              {/* Add to Cart or Update */}
              <button
                onClick={handleAddToCart}
                className={`w-[60%] h-[45px] ${(loading || isAddingToCartRef.current) ? 'bg-gray-300 cursor-not-allowed' : 'cursor-pointer'} text-white font-semibold text-sm rounded-xl px-3 flex items-center justify-between`}
                style={{ 
                  backgroundColor: (loading || isAddingToCartRef.current) ? undefined : primaryColor,
                  borderColor: (loading || isAddingToCartRef.current) ? undefined : primaryColor
                }}
                disabled={loading || isAddingToCartRef.current}
              >
                {(loading || isAddingToCartRef.current) ? (
                  <div className="flex items-center justify-center w-full gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t("menu.addingToCart")}</span>
                  </div>
                ) : (
                  <>
                    <span>{isEditing ? t('common.update') : t('menu.addToCart')}</span>
                    <div className="flex items-center gap-1.5" dir="ltr">
                      <CurrencyDisplay
                        src="/SAR-w.svg"
                        className="w-4 h-4"
                        iconStyle={{ filter: `brightness(0) invert(1)` }}
                      />
                      <span>{formatNum(totalPrice * quantity)}</span>
                    </div>
                  </>
                )}
              </button>
            </div>
            )}
          </>
        )}
      </div>
      
      {/* Phone Number Popup */}
      {showPhonePopup && tempItem && (
        <PhoneNumberPopup
          onClose={() => {
            setShowPhonePopup(false)
            setTempItem(null)
          }}
          onSubmit={(phoneNumber: string) => {
            // Pass the temp item to the success handler after phone registration
            handlePhoneLoginSuccess(tempItem)
          }}
        />
      )}
    </div>
  )
}
